/**
 * MobileAppearanceSection Component
 * 
 * Collapsible theme/layout section for the mobile hamburger menu.
 * Display mode toggle has been promoted to the header bar.
 */
import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { setTheme, setLayoutPreference } from '../../slices/pageLayoutSettingsSlice.mjs';
import useCurrentPageType from '../../hooks/useCurrentPageType.mjs';
import { setGuestLayoutPreferences } from '../../utils/layoutPreferencesStorage.mjs';
import { AVAILABLE_THEMES, LAYOUTS_BY_TYPE } from './AppearanceHub.mjs';

// =============================================================================
// Main Component
// =============================================================================

export default function MobileAppearanceSection() {
    const dispatch = useDispatch();
    const [isExpanded, setIsExpanded] = useState(false);
    const [showThemePicker, setShowThemePicker] = useState(false);
    const [showLayoutPicker, setShowLayoutPicker] = useState(false);

    // Redux state
    const resolvedTheme = useSelector(state => state.pageLayoutSettings?.resolvedTheme);
    const currentThemeName = resolvedTheme?.name || 'default';
    const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
    const userLayoutPreferences = useSelector(state => state.pageLayoutSettings?.userLayoutPreferences || {});

    // Current page type for contextual layouts
    const { pageType, label: pageTypeLabel } = useCurrentPageType();
    const layouts = pageType ? LAYOUTS_BY_TYPE[pageType] : [];
    const currentLayout = userLayoutPreferences[pageType] || 'default';

    // Get current theme info
    const currentThemeInfo = AVAILABLE_THEMES.find(t => t.name === currentThemeName) || AVAILABLE_THEMES[0];
    const currentLayoutInfo = layouts.find(l => l.id === currentLayout) || layouts[0];

    const handleThemeChange = useCallback(async (theme) => {
        dispatch(setTheme({ name: theme.name, version: null }));

        if (typeof document !== 'undefined') {
            const html = document.documentElement;
            if (theme.name === 'default') {
                html.removeAttribute('data-theme');
            } else {
                html.setAttribute('data-theme', theme.name);
            }
        }

        if (isAuthenticated) {
            try {
                await axios.patch('/q/api/user-preferences', {
                    selectedTheme: theme.name === 'default' ? null : theme.name
                });
            } catch (error) {
                console.warn('Failed to persist theme preference:', error.message);
            }
        }

        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('selectedTheme', theme.name);
        }

        setShowThemePicker(false);
    }, [dispatch, isAuthenticated]);

    const handleLayoutChange = useCallback(async (layoutId) => {
        if (!pageType) return;

        dispatch(setLayoutPreference({ pageType, layout: layoutId }));

        if (isAuthenticated) {
            try {
                await axios.patch('/q/api/user-preferences', {
                    layouts: { [pageType]: layoutId }
                });
            } catch (error) {
                console.warn('Failed to persist layout preference:', error.message);
            }
        } else {
            setGuestLayoutPreferences({ [pageType]: layoutId });
        }

        setShowLayoutPicker(false);
    }, [dispatch, isAuthenticated, pageType]);

    return (
        <div className="mobile-appearance-section">
            {/* Collapsible Header */}
            <button
                className="mobile-appearance-header"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
            >
                <span className="header-content">
                    <span>🎨</span>
                    <span>Theme & Layout</span>
                </span>
                <svg
                    className={`chevron ${isExpanded ? 'expanded' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Expandable Content */}
            {isExpanded && (
                <div className="mobile-appearance-content">
                    {/* Theme Row */}
                    <div className="mobile-setting-row">
                        <span className="setting-label">Theme</span>
                        <div className="mobile-setting-control">
                            <button
                                className="mobile-compact-dropdown"
                                onClick={() => setShowThemePicker(!showThemePicker)}
                                aria-expanded={showThemePicker}
                            >
                                <span>{currentThemeInfo.icon}</span>
                                <span>{currentThemeInfo.label}</span>
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Theme Picker Dropdown */}
                    {showThemePicker && (
                        <div className="mobile-picker-dropdown">
                            {AVAILABLE_THEMES.map(theme => (
                                <button
                                    key={theme.name}
                                    className={`mobile-picker-option ${currentThemeName === theme.name ? 'selected' : ''}`}
                                    onClick={() => handleThemeChange(theme)}
                                >
                                    <span className="option-icon">{theme.icon}</span>
                                    <span className="option-label">{theme.label}</span>
                                    {currentThemeName === theme.name && (
                                        <svg className="check-icon" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Layout Row (Contextual) */}
                    {pageType && layouts.length > 0 && (
                        <>
                            <div className="mobile-setting-row">
                                <span className="setting-label">{pageTypeLabel} Layout</span>
                                <div className="mobile-setting-control">
                                    <button
                                        className="mobile-compact-dropdown"
                                        onClick={() => setShowLayoutPicker(!showLayoutPicker)}
                                        aria-expanded={showLayoutPicker}
                                    >
                                        <span>{currentLayoutInfo?.icon}</span>
                                        <span>{currentLayoutInfo?.name}</span>
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Layout Picker Dropdown */}
                            {showLayoutPicker && (
                                <div className="mobile-picker-dropdown">
                                    {layouts.map(layout => (
                                        <button
                                            key={layout.id}
                                            className={`mobile-picker-option ${currentLayout === layout.id ? 'selected' : ''}`}
                                            onClick={() => handleLayoutChange(layout.id)}
                                        >
                                            <span className="option-icon">{layout.icon}</span>
                                            <div className="option-text">
                                                <span className="option-label">{layout.name}</span>
                                                <span className="option-desc">{layout.description}</span>
                                            </div>
                                            {currentLayout === layout.id && (
                                                <svg className="check-icon" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
